/**
 * "Pushimet e mia" — read-only leave summary.
 *
 * Informational only: no create, edit or delete. Applying for leave lives in
 * the leave form at /holidays/addholiday (reachable from the "Kërko Leje"
 * sidebar entry, EmployeeHome and the Approval Center) — that flow is
 * deliberately untouched.
 *
 * Every number comes from GET /api/my-leave-summary, which resolves the worker
 * from the JWT. The page never asks for a workerId and never filters someone
 * else's rows out in the browser: the old version fetched GET /api/holidays
 * (every worker's leave) and filtered client-side, so other people's requests
 * were sitting in the response.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Tooltip,
  Skeleton, Alert, AlertTitle, Button, IconButton,
} from '@mui/material';
import BeachAccessIcon    from '@mui/icons-material/BeachAccess';
import InfoOutlinedIcon   from '@mui/icons-material/InfoOutlined';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { authFetch }      from '../services/apiServices';

/* ─── shared meta (unchanged from the previous version) ───────────────────── */
const STATUS_META = {
  pending_supervisor:  { label: 'Pret supervizorin', dot: '#F59E0B', fg: '#D97706' },
  pending_hr:          { label: 'Pret HR',           dot: '#3B82F6', fg: '#1D4ED8' },
  approved:            { label: 'Aprovuar',          dot: '#22C55E', fg: '#16A34A' },
  supervisor_rejected: { label: 'Refuzuar (sup.)',   dot: '#EF4444', fg: '#B91C1C' },
  hr_rejected:         { label: 'Refuzuar (HR)',     dot: '#DC2626', fg: '#9F1239' },
};

const TYPES = [
  { id: 1, name: 'Vjetor',           color: '#1D4ED8', dot: '#3B82F6' },
  { id: 6, name: 'Me pagesë',        color: '#15803D', dot: '#22C55E' },
  { id: 4, name: 'Pa pagesë',        color: '#4B5563', dot: '#9CA3AF' },
  { id: 2, name: 'Mjekësor',         color: '#B45309', dot: '#F59E0B' },
  { id: 3, name: 'Martesor',         color: '#0369A1', dot: '#0EA5E9' },
  { id: 5, name: 'Jashtëzakonshëm', color: '#3F6212', dot: '#84CC16' },
  { id: 7, name: 'Dhënie gjaku',     color: '#9D174D', dot: '#EC4899' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—');

/** Up to 2 decimals, trailing zeros trimmed (2.80 → "2.8"); keeps a leading sign. */
const fmtDays = (n) => {
  const v = Number(n) || 0;
  const r = Math.round(v * 100) / 100;
  return r.toFixed(2).replace(/\.?0+$/, '');
};
const fmtSigned = (n) => ((Number(n) || 0) > 0 ? `+${fmtDays(n)}` : fmtDays(n));

/* ─── page ────────────────────────────────────────────────────────────────── */
export default function MyLeavesPage() {
  const [summary, setSummary] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [page, setPage]       = useState(0);
  const [rpp, setRpp]         = useState(10);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [sRes, lRes] = await Promise.all([
        authFetch('/api/my-leave-summary'),
        authFetch('/api/my-leaves'),
      ]);
      const sJson = await sRes.json().catch(() => ({}));
      const lJson = await lRes.json().catch(() => ({}));

      if (!sRes.ok || sJson.success === false) {
        // A user with no linked worker record gets a specific, actionable
        // message rather than an empty page that looks broken.
        setError(sJson.error || sJson.errorMessage || `Nuk u morën të dhënat (HTTP ${sRes.status}).`);
        setSummary(null);
        setRequests([]);
        return;
      }
      setSummary(sJson.data || null);
      setRequests(Array.isArray(lJson.data) ? lJson.data : []);
    } catch (e) {
      setError('Nuk u lidh dot me serverin. Kontrollo lidhjen dhe provo përsëri.');
      setSummary(null);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const thSx = {
    fontWeight: 700, fontSize: '0.7rem', py: 1, px: 1.5,
    color: '#6B7280', bgcolor: '#F9FAFB', letterSpacing: '0.05em',
    textTransform: 'uppercase', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap',
  };
  const tdSx = { py: 0.75, px: 1.5, fontSize: '0.82rem', borderBottom: '1px solid #F3F4F6' };

  /* ── error state ── */
  if (!loading && error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Alert
          severity="error"
          sx={{ fontSize: '0.85rem', borderRadius: 2 }}
          action={
            <Button size="small" startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />} onClick={load}
              sx={{ fontSize: '0.78rem', textTransform: 'none', fontWeight: 700 }}>
              Provo përsëri
            </Button>
          }>
          <AlertTitle sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Nuk u shfaqën pushimet</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Summary ── */}
      <SummaryPanel summary={summary} loading={loading} onRefresh={load} />

      {/* ── Requests (read-only) ── */}
      <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151' }}>
            Kërkesat e mia
          </Typography>
          <Chip label="Vetëm shikim" size="small"
            sx={{ height: 20, fontSize: '0.66rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#64748B' }} />
          <Typography variant="caption" sx={{ ml: 'auto', fontSize: '0.72rem', color: '#6B7280', fontVariantNumeric: 'tabular-nums' }}>
            {loading ? '—' : `${requests.length} kërkesa`}
          </Typography>
        </Box>

        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Lloji</TableCell>
                <TableCell sx={thSx}>Perioda</TableCell>
                <TableCell sx={{ ...thSx, display: { xs: 'none', sm: 'table-cell' } }}>Ditë</TableCell>
                <TableCell sx={thSx}>Statusi</TableCell>
                <TableCell sx={{ ...thSx, display: { xs: 'none', md: 'table-cell' } }}>Shënim</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {[100, 140, 40, 90, 140].map((w, j) => (
                      <TableCell key={j} sx={tdSx}><Skeleton width={w} height={16} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <BeachAccessIcon sx={{ fontSize: 36, color: '#E5E7EB', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography variant="body2" color="text.disabled">
                      Nuk keni asnjë kërkesë pushimi
                    </Typography>
                    {/* No "apply" call to action here on purpose — this module is
                        informational; applying lives in the leave form. */}
                    <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mt: 0.5 }}>
                      Kërkesat e reja bëhen te “Kërko Leje”.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                requests.slice(page * rpp, page * rpp + rpp).map((h) => {
                  const ht = TYPES.find((t) => t.id === h.HolidayType);
                  const m  = STATUS_META[h.Status] || STATUS_META.pending_supervisor;
                  const isRejected = h.Status === 'supervisor_rejected' || h.Status === 'hr_rejected';
                  return (
                    <TableRow key={h.Id} hover sx={{ '&:hover td': { bgcolor: '#FAFAFA' } }}>
                      <TableCell sx={tdSx}>
                        {ht ? (
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: ht.dot, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '0.78rem', color: ht.color, fontWeight: 600 }}>{ht.name}</Typography>
                          </Stack>
                        ) : <Typography sx={{ fontSize: '0.78rem', color: '#D1D5DB' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#374151', fontVariantNumeric: 'tabular-nums' }} noWrap>
                          {fmtDate(h.StartDate)}&nbsp;–&nbsp;{fmtDate(h.EndDate)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, display: { xs: 'none', sm: 'table-cell' } }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 34, height: 22, borderRadius: 1, bgcolor: '#F3F4F6', px: 0.75 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtDays(h.holidayDays || 0)}d
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tdSx}>
                        <Stack direction="row" alignItems="center" spacing={0.6}>
                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: m.dot }} />
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: m.fg }}>{m.label}</Typography>
                          {isRejected && h.RejectedReason && (
                            <Tooltip title={h.RejectedReason} placement="top">
                              <InfoOutlinedIcon sx={{ fontSize: 14, color: m.fg, opacity: 0.6 }} />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, display: { xs: 'none', md: 'table-cell' }, maxWidth: 200 }}>
                        <Tooltip title={h.Note || ''} placement="top">
                          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }} noWrap>
                            {h.Note || <Box component="span" sx={{ color: '#E5E7EB' }}>—</Box>}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {requests.length > rpp && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div" count={requests.length} rowsPerPage={rpp} page={page}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRpp(+e.target.value); setPage(0); }}
            labelRowsPerPage="Rreshta:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} nga ${count}`}
            sx={{ fontSize: '0.75rem', borderTop: '1px solid #F3F4F6', '& .MuiTablePagination-select': { fontSize: '0.75rem' } }}
          />
        )}
      </Paper>
    </Box>
  );
}

/* ─── Summary panel ───────────────────────────────────────────────────────── */
export function SummaryPanel({ summary, loading, onRefresh }) {
  const s = summary || {};

  // Order follows how the balance is actually spent: what you are granted,
  // then what leaves it, then what is left.
  const granted = [
    { key: 'entitlementDays',  label: 'Ditët që ju takojnë', value: s.entitlementDays,  accent: '#1D4ED8' },
    { key: 'carriedOverDays',  label: 'Ditët e bartura',      value: s.carriedOverDays,  accent: '#B45309' },
    { key: 'adjustmentDays',   label: 'Korrigjim manual',     value: s.adjustmentDays,   accent: '#3E8FA3', signed: true },
  ];
  const spent = [
    { key: 'usedDays',             label: 'Ditët e përdorura',   value: s.usedDays,             accent: '#374151' },
    { key: 'approvedUpcomingDays', label: 'Aprovuar, pa përdorur', value: s.approvedUpcomingDays, accent: '#0369A1' },
    { key: 'pendingDays',          label: 'Në pritje',            value: s.pendingDays,          accent: '#D97706' },
  ];

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden' }}>
      {/* Header — period is part of the summary, not decoration */}
      <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <BeachAccessIcon sx={{ fontSize: 18, color: '#6366F1' }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151' }}>
          Përmbledhja e pushimeve
        </Typography>
        {loading ? (
          <Skeleton width={130} height={20} />
        ) : (
          <Chip
            size="small"
            label={s.year ? `Viti ${s.year} · ${fmtDate(s.periodStart)} – ${fmtDate(s.periodEnd)}` : '—'}
            sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: 'primary.soft', color: 'primary.onSoft' }}
          />
        )}
        {s.workerName && !loading && (
          <Typography sx={{ fontSize: '0.75rem', color: '#6B7280' }}>{s.workerName}</Typography>
        )}
        {/* span wrapper: a disabled button fires no events, so MUI cannot
            hang the tooltip off it directly. */}
        {onRefresh && (
          <Tooltip title="Rifresko">
            <span style={{ marginLeft: 'auto' }}>
              <IconButton size="small" onClick={onRefresh} disabled={loading}
                sx={{ width: 26, height: 26, color: '#64748B' }}>
                <RefreshRoundedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box sx={{ p: 1.5, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1.5, alignItems: 'stretch' }}>
        <StatGroup title="Të drejtat" items={granted} loading={loading} />
        <StatGroup title="Të shpenzuara" items={spent} loading={loading} />

        {/* Remaining — the number people actually come here for */}
        <Box sx={{
          flex: '0 0 auto', minWidth: { xs: '100%', md: 190 },
          display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
          px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid #BBF7D0', bgcolor: '#F0FDF4',
        }}>
          {loading ? (
            <Skeleton width={70} height={40} />
          ) : (
            <Typography sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1, color: '#16A34A', fontVariantNumeric: 'tabular-nums' }}>
              {fmtDays(s.remainingDays)}
              <Box component="span" sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#4B5563', ml: 0.5 }}>ditë</Box>
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#15803D', mt: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Ditët e mbetura
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: '#6B7280', mt: 0.25, textAlign: 'center' }}>
            Përfshin ditët në pritje
          </Typography>
        </Box>
      </Box>

      {/* Empty state — a real balance with nothing spent yet is not an error */}
      {!loading && summary && s.requestCount === 0 && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Alert severity="info" icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
            sx={{ py: 0.25, fontSize: '0.78rem', borderRadius: 1.5 }}>
            Ende pa kërkesa pushimi për këtë vit — e gjithë kuota është e lirë.
          </Alert>
        </Box>
      )}
    </Paper>
  );
}

function StatGroup({ title, items, loading }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.75 }}>
        {title}
      </Typography>
      <Stack spacing={0.75}>
        {items.map((it) => (
          <Box key={it.key} sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            px: 1.25, py: 0.85, borderRadius: 1.5, border: '1px solid #EEF0F3', bgcolor: '#FAFBFC',
          }}>
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, color: '#6B7280' }}>{it.label}</Typography>
            {loading ? (
              <Skeleton width={44} height={20} />
            ) : (
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: it.accent, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {it.signed ? fmtSigned(it.value) : fmtDays(it.value)}
                <Box component="span" sx={{ fontSize: '0.66rem', fontWeight: 600, color: '#9CA3AF', ml: 0.4 }}>d</Box>
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
